import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { auth, db, storage } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject,
} from "firebase/storage";
import { Container, Main, Text, Title } from "./studentDrawerKonularim";
import ReactTable2 from "../../../components/tables/ReactTable2";
import Loading from "../../../components/Loading";

const StudentDrawerOdevlerim = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHomeworks = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setHomeworks(data.homeworks || []);
        }
      } catch (error) {
        console.error("Ödevler alınırken hata oluştu:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworks();
  }, []);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (homeworks.length > 0 && currentUser?.uid) {
      fetchUploadedFiles(homeworks, currentUser.uid);
    }
  }, [homeworks]);

  const fetchUploadedFiles = async (homeworks, studentUid) => {
    setLoading(true);
    const fileMap = {};
    for (const item of homeworks) {
      const folderRef = ref(
        storage,
        `homework-files/${studentUid}/${item.id}/`
      );
      try {
        const res = await listAll(folderRef);
        const files = await Promise.all(
          res.items.map(async (fileRef) => ({
            name: fileRef.name,
            url: await getDownloadURL(fileRef),
            fullRef: fileRef,
          }))
        );
        fileMap[item.id] = files;
      } catch {
        fileMap[item.id] = [];
      }
    }
    setUploadedFiles(fileMap);
    setLoading(false);
  };

  const handleDeleteFile = async (homeworkId, fileRef) => {
    setLoading(true);
    try {
      await deleteObject(fileRef);
      const currentUser = auth.currentUser;
      if (currentUser?.uid) {
        fetchUploadedFiles(homeworks, currentUser.uid);
      }
    } catch (err) {
      console.error("Dosya silme hatası:", err);
      alert("Dosya silinemedi.");
      setLoading(false);
    }
  };

  const handleFileChange = (e, homeworkId) => {
    const file = e.target.files[0];
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "image/jpeg",
      "image/png",
    ];
    if (file && allowedTypes.includes(file.type)) {
      setSelectedFiles((prev) => ({ ...prev, [homeworkId]: file }));
    } else {
      alert("Desteklenmeyen dosya türü.");
      e.target.value = null;
    }
  };

  const handleUpload = async (homeworkId) => {
    const currentUser = auth.currentUser;
    const file = selectedFiles[homeworkId];
    if (!currentUser || !file) return;

    setLoading(true);
    const filePath = `homework-files/${
      currentUser.uid
    }/${homeworkId}/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, filePath);

    try {
      await uploadBytes(fileRef, file);
      alert("Dosya başarıyla yüklendi!");
      await fetchUploadedFiles(homeworks, currentUser.uid);
      setSelectedFiles((prev) => ({ ...prev, [homeworkId]: null }));
    } catch (error) {
      console.error("Yükleme hatası:", error);
      alert("Yükleme sırasında bir hata oluştu.");
      setLoading(false);
    }
  };

  const formatDate = (dateString) => dateString?.split("-").join(".");

  const columns = [
    { header: "No", accessorKey: "no" },
    { header: "Kazanım", accessorKey: "kazanim" },
    { header: "Sınıf", accessorKey: "className" },
    { header: "Ünite", accessorKey: "unit" },
    { header: "Soru Sayısı", accessorKey: "soruSayisi" },
    { header: "Başlangıç", accessorKey: "startDate" },
    { header: "Bitiş", accessorKey: "endDate" },
    { header: "Durum", accessorKey: "durum" },
    {
      header: "Dosya Ekle",
      cell: (row) => (
        <UploadWrapper>
          <RowButtons>
            <FileInputLabel htmlFor={`file-${row.id}`}>
              Dosya Seç
            </FileInputLabel>
            <HiddenFileInput
              id={`file-${row.id}`}
              type="file"
              accept=".pdf,.docx,.xlsx,.pptx,.png,.jpg,.jpeg"
              onChange={(e) => handleFileChange(e, row.id)}
            />
            <UploadButton onClick={() => handleUpload(row.id)}>
              Yükle
            </UploadButton>
          </RowButtons>

          {selectedFiles[row.id] && (
            <SelectedFileName>
              Seçilen: {selectedFiles[row.id].name}
            </SelectedFileName>
          )}

          {uploadedFiles[row.id]?.length > 0 && (
            <FileList>
              {uploadedFiles[row.id].map((file, idx) => (
                <FileItem key={idx}>
                  <FileLink
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Dosya {idx + 1}
                  </FileLink>
                  <DeleteButton
                    onClick={() => handleDeleteFile(row.id, file.fullRef)}
                  >
                    Sil
                  </DeleteButton>
                </FileItem>
              ))}
            </FileList>
          )}
        </UploadWrapper>
      ),
    },
  ];

  const data = homeworks.map((hw, index) => ({
    id: hw.id,
    no: index + 1,
    kazanim: hw.kazanims || "-",
    className: hw.className || "-",
    unit: hw.unit || "-",
    soruSayisi: hw.soruSayisi || "-",
    startDate: formatDate(hw.startDate),
    endDate: formatDate(hw.endDate),
    durum: hw.bittiMi === 1 ? "Tamamlandı" : "Tamamlanmadı",
  }));

  if (loading) return <Loading />;

  return (
    <Container>
      <Title>
        <Text>Ödevlerim</Text>
      </Title>
      <Main>
        <ReactTable2 data={data} columns={columns} />
      </Main>
    </Container>
  );
};

// Styled Components
const UploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const RowButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const FileInputLabel = styled.label`
  background-color: var(--main-color);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  text-align: center;

  &:hover {
    opacity: 0.9;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadButton = styled.button`
  background-color: var(--main-color);
  color: white;
  font-size: 0.8rem;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const SelectedFileName = styled.div`
  font-size: 0.75rem;
  color: var(--main-color);
  margin-top: 4px;
`;

const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
`;

const FileItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  background: #f8f8f8;
  padding: 6px 10px;
  border-radius: 4px;
  margin-bottom: 4px;
`;

const FileLink = styled.a`
  color: var(--main-color);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  color: var(--delete-color);
  border: none;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export default StudentDrawerOdevlerim;
